"use client";

import * as React from "react";
import {
  AlertTriangle,
  Check,
  ChevronsUpDown,
  InboxIcon,
  Loader2,
  Search,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import { usePermissions } from "@/context/permissions-context";
import { fetchData } from "@/utils/api-handler";
import { IntegrationMetaBadge } from "@/components/integrations/ui/integration-meta-badge";
import {
  formatIntegrationDisplayName,
  getIntegrationReferenceLabel,
} from "@/utils/integration-status";

export type AppListSelection = {
  app_integrations: string[];
  custom_apps: string[];
};

export type AppListVariant = "combined" | "integrations" | "custom_apps";

interface AppListProps {
  onAppSelect?: (appIds: string[]) => void;
  defaultValue?: string[];
  onAppsChange?: (selection: AppListSelection) => void;
  defaultIntegrationIds?: string[];
  defaultCustomAppIds?: string[];
  onIntegrationIdsChange?: (integrationIds: string[]) => void;
  onCustomAppIdsChange?: (customAppIds: string[]) => void;
  connectedIntegrations?: ConnectedIntegration[];
  connectedCustomApps?: ConnectedCustomApp[];
  variant?: AppListVariant;
  triggerPlaceholder?: string;
  className?: string;
}

function toggleId(ids: string[], id: string) {
  return ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];
}

export function AppList({
  onAppSelect,
  defaultValue,
  onAppsChange,
  defaultIntegrationIds,
  defaultCustomAppIds,
  onIntegrationIdsChange,
  onCustomAppIdsChange,
  connectedIntegrations,
  connectedCustomApps,
  variant: variantProp,
  triggerPlaceholder,
  className,
}: AppListProps) {
  const variant: AppListVariant =
    variantProp ??
    (onIntegrationIdsChange
      ? "integrations"
      : onCustomAppIdsChange
        ? "custom_apps"
        : "combined");

  const integrationsQueryEnabled =
    variant === "combined" || variant === "integrations";
  const customAppsQueryEnabled =
    variant === "combined" || variant === "custom_apps";

  const usesSplitSelection =
    (variant === "combined" && Boolean(onAppsChange)) ||
    (variant === "integrations" && Boolean(onIntegrationIdsChange)) ||
    (variant === "custom_apps" && Boolean(onCustomAppIdsChange));

  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const { hasAllPermissions } = usePermissions();
  const canListIntegrations = hasAllPermissions(["LIST_APP_INTEGRATIONS"]);
  const canListCustomApps = hasAllPermissions(["LIST_ORG_CUSTOM_APPS"]);

  // Stabilize the defaultValue (array of tool IDs)
  const stableDefaultValue = React.useMemo(() => {
    return Array.isArray(defaultValue) ? defaultValue : [];
  }, [defaultValue]);

  const [selectedApps, setSelectedApps] = React.useState<string[]>([]);
  const [splitSelection, setSplitSelection] = React.useState<{
    integrationIds: string[];
    customIds: string[];
  }>({ integrationIds: [], customIds: [] });

  const { ref, inView } = useInView();

  const {
    data,
    isLoading: isLoadingIntegrations,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<IntegrationListPageResponse>({
    queryKey: ["integrations-apps", "app-list"],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({ page: String(pageParam), limit: "50" });
      return fetchData(`/api/integrations/apps?${params.toString()}`);
    },
    getNextPageParam: (lastPage, pages) => {
      if (pages.length < lastPage.page_count) {
        return pages.length + 1;
      }
      return undefined;
    },
    enabled: integrationsQueryEnabled && canListIntegrations,
  });

  const {
    data: customAppsData,
    isLoading: isLoadingCustomApps,
    fetchNextPage: fetchNextCustomAppsPage,
    hasNextPage: hasNextCustomAppsPage,
    isFetchingNextPage: isFetchingNextCustomAppsPage,
  } = useInfiniteQuery<CustomAppListPageResponse>({
    queryKey: ["integrations-custom-apps", "app-list"],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({ page: String(pageParam), limit: "50" });
      return fetchData(`/api/integrations/custom-apps?${params.toString()}`);
    },
    getNextPageParam: (lastPage, pages) => {
      if (pages.length < lastPage.page_count) {
        return pages.length + 1;
      }
      return undefined;
    },
    enabled: customAppsQueryEnabled && canListCustomApps,
  });

  const isLoading =
    (integrationsQueryEnabled && isLoadingIntegrations) ||
    (customAppsQueryEnabled && isLoadingCustomApps);

  const catalogTools = React.useMemo(() => {
    const integrationItems = (data?.pages.flatMap((page) => page.items) ?? [])
      .filter((item) => item.status === "ACTIVE" || item.status === "EXPIRED")
      .map((item) => {
        const desc = item.description?.trim();
        const alias = item.alias?.trim();
        const row = item as IntegrationListResponse & {
          integration_description?: string;
          app_description?: string;
        };
        return {
          kind: "integration" as const,
          id: item.id,
          status: item.status,
          title: item.third_party_app.name,
          alias: alias || null,
          subtitle: getIntegrationReferenceLabel(
            row.integration_description,
            desc
          ),
          logoUrl: item.third_party_app.app_logo_url ?? "",
          description: item.description,
          integration_description: row.integration_description,
          app_description: row.app_description,
          owner: item.owner,
          visibility: item.visibility,
        };
      });

    const customItems = (
      customAppsData?.pages.flatMap((page) => page.items) ?? []
    )
      .filter((item) => {
        const s = String(item.status).toUpperCase();
        return s === "ACTIVE";
      })
      .map((item) => {
        const customDesc = (item.tool_config?.description ?? "").trim();
        return {
          kind: "custom_app" as const,
          id: item.id,
          status: item.status,
          title: item.name,
          subtitle: customDesc,
          logoUrl: "",
          type: item.type,
          owner: item.owner,
          visibility: item.visibility,
        };
      });

    if (variant === "integrations") {
      return integrationItems;
    }
    if (variant === "custom_apps") {
      return customItems;
    }
    return [...integrationItems, ...customItems];
  }, [data, customAppsData, variant]);

  const selectedIntegrationIds = usesSplitSelection
    ? splitSelection.integrationIds
    : selectedApps;
  const selectedCustomIds = usesSplitSelection
    ? splitSelection.customIds
    : selectedApps;
  const selectedIdSet = React.useMemo(
    () => new Set([...selectedIntegrationIds, ...selectedCustomIds]),
    [selectedIntegrationIds, selectedCustomIds]
  );

  const attachedTools = React.useMemo(() => {
    const integrationItems =
      variant === "custom_apps"
        ? []
        : (connectedIntegrations ?? [])
            .map((item) => {
              const id = item.integration_id ?? item.id;
              if (!id || !selectedIntegrationIds.includes(id)) return null;
              const catalogMatch = catalogTools.find(
                (tool) => tool.kind === "integration" && tool.id === id
              );
              const alias =
                item.alias?.trim() ||
                (catalogMatch && "alias" in catalogMatch
                  ? catalogMatch.alias
                  : null) ||
                null;
              return {
                kind: "integration" as const,
                id,
                status: catalogMatch?.status ?? item.status ?? "ACTIVE",
                title: item.name ?? "App",
                alias,
                subtitle: getIntegrationReferenceLabel(
                  item.integration_description,
                  catalogMatch?.kind === "integration"
                    ? catalogMatch.description
                    : undefined
                ),
                logoUrl: item.app_logo_url ?? "",
                integration_description: item.integration_description,
                app_description: item.app_description,
                owner: item.owner,
                visibility: item.visibility,
                isAttached: true,
              };
            })
            .filter((item): item is NonNullable<typeof item> => item !== null);

    const customItems =
      variant === "integrations"
        ? []
        : (connectedCustomApps ?? [])
            .map((item) => {
              const id = item.custom_app_id ?? item.id;
              if (!id || !selectedCustomIds.includes(id)) return null;
              const catalogStatus = catalogTools.find(
                (tool) => tool.kind === "custom_app" && tool.id === id
              )?.status;
              return {
                kind: "custom_app" as const,
                id,
                status: catalogStatus ?? item.status ?? "ACTIVE",
                title: item.name ?? id,
                subtitle: item.description?.trim() ?? "",
                logoUrl: "",
                type: item.type,
                owner: item.owner,
                visibility: item.visibility,
                isAttached: true,
              };
            })
            .filter((item): item is NonNullable<typeof item> => item !== null);

    return [...integrationItems, ...customItems];
  }, [
    catalogTools,
    connectedIntegrations,
    connectedCustomApps,
    selectedIntegrationIds,
    selectedCustomIds,
    variant,
  ]);

  const attachedToolIds = React.useMemo(
    () => new Set(attachedTools.map((tool) => tool.id)),
    [attachedTools]
  );

  const pendingSelectedCatalogTools = React.useMemo(
    () =>
      catalogTools
        .filter((tool) => selectedIdSet.has(tool.id) && !attachedToolIds.has(tool.id))
        .map((tool) => ({ ...tool, isAttached: true })),
    [attachedToolIds, catalogTools, selectedIdSet]
  );

  const attachedGroupTools = React.useMemo(
    () => [...attachedTools, ...pendingSelectedCatalogTools],
    [attachedTools, pendingSelectedCatalogTools]
  );

  const availableTools = React.useMemo(
    () => catalogTools.filter((tool) => !selectedIdSet.has(tool.id)),
    [catalogTools, selectedIdSet]
  );

  const allTools = React.useMemo(
    () => [...attachedGroupTools, ...availableTools],
    [attachedGroupTools, availableTools]
  );

  React.useEffect(() => {
    if (!inView) return;
    if (integrationsQueryEnabled && canListIntegrations && hasNextPage) {
      fetchNextPage();
    } else if (customAppsQueryEnabled && canListCustomApps && hasNextCustomAppsPage) {
      fetchNextCustomAppsPage();
    }
  }, [
    inView,
    integrationsQueryEnabled,
    customAppsQueryEnabled,
    fetchNextPage,
    hasNextPage,
    fetchNextCustomAppsPage,
    hasNextCustomAppsPage,
    canListIntegrations,
    canListCustomApps,
  ]);

  React.useEffect(() => {
    if (usesSplitSelection) return;
    setSelectedApps(stableDefaultValue);
  }, [stableDefaultValue, usesSplitSelection]);

  React.useEffect(() => {
    if (!usesSplitSelection) return;
    if (variant === "integrations") {
      setSplitSelection({
        integrationIds: [...(defaultIntegrationIds ?? [])],
        customIds: [],
      });
      return;
    }
    if (variant === "custom_apps") {
      setSplitSelection({
        integrationIds: [],
        customIds: [...(defaultCustomAppIds ?? [])],
      });
      return;
    }
    setSplitSelection({
      integrationIds: [...(defaultIntegrationIds ?? [])],
      customIds: [...(defaultCustomAppIds ?? [])],
    });
  }, [
    usesSplitSelection,
    variant,
    defaultIntegrationIds,
    defaultCustomAppIds,
  ]);

  const handleSelect = (appId: string) => {
    const tool = allTools.find((t) => t.id === appId);
    if (!tool) return;

    if (variant === "integrations" && onIntegrationIdsChange) {
      const integrationIds = toggleId(splitSelection.integrationIds, appId);
      setSplitSelection((prev) => ({ ...prev, integrationIds }));
      onIntegrationIdsChange(integrationIds);
      return;
    }
    if (variant === "custom_apps" && onCustomAppIdsChange) {
      const customIds = toggleId(splitSelection.customIds, appId);
      setSplitSelection((prev) => ({ ...prev, customIds }));
      onCustomAppIdsChange(customIds);
      return;
    }
    if (variant === "combined" && onAppsChange) {
      const isIntegration = tool.kind === "integration";
      const integrationIds = isIntegration
        ? toggleId(splitSelection.integrationIds, appId)
        : splitSelection.integrationIds;
      const customIds = !isIntegration
        ? toggleId(splitSelection.customIds, appId)
        : splitSelection.customIds;
      setSplitSelection({
        integrationIds,
        customIds,
      });
      onAppsChange({
        app_integrations: integrationIds,
        custom_apps: customIds,
      });
      return;
    }

    setSelectedApps((prev) => {
      const newSelectedApps = prev.includes(appId)
        ? prev.filter((id) => id !== appId)
        : [...prev, appId];
      onAppSelect?.(newSelectedApps);
      return newSelectedApps;
    });
  };

  const isRowSelected = (tool: (typeof allTools)[number]) => {
    if (usesSplitSelection) {
      if (variant === "integrations") {
        return (
          tool.kind === "integration" &&
          splitSelection.integrationIds.includes(tool.id)
        );
      }
      if (variant === "custom_apps") {
        return (
          !(tool.kind === "integration") &&
          splitSelection.customIds.includes(tool.id)
        );
      }
      return tool.kind === "integration"
        ? splitSelection.integrationIds.includes(tool.id)
        : splitSelection.customIds.includes(tool.id);
    }
    return selectedApps.includes(tool.id);
  };

  const matchesSearch = (tool: (typeof allTools)[number]) => {
    const q = searchQuery.toLowerCase();
    const alias =
      tool.kind === "integration" && "alias" in tool && tool.alias
        ? tool.alias.toLowerCase()
        : "";
    return (
      tool.title.toLowerCase().includes(q) ||
      tool.subtitle.toLowerCase().includes(q) ||
      alias.includes(q)
    );
  };
  const filteredAttachedTools = attachedGroupTools.filter(matchesSearch);
  const filteredAvailableTools = availableTools.filter(matchesSearch);
  const hasFilteredTools =
    filteredAttachedTools.length > 0 || filteredAvailableTools.length > 0;

  const renderToolRow = (tool: (typeof allTools)[number], group: "attached" | "available") => {
    const st = String(tool.status).toUpperCase();
    const isActive = st === "ACTIVE";
    const isExpired = st === "EXPIRED";
    const selected = isRowSelected(tool);
    const isAttached = group === "attached";

    return (
      <div
        key={`${group}-${tool.id}`}
        role="option"
        aria-selected={selected}
        onClick={(e) => {
          e.stopPropagation();
          handleSelect(tool.id);
        }}
        className={cn(
          "flex items-start w-full gap-2 px-2 py-2 text-sm rounded-md cursor-pointer select-none transition-colors",
          isAttached
            ? "hover:bg-accent hover:text-accent-foreground"
            : selected
              ? "bg-accent text-accent-foreground"
              : "hover:bg-accent hover:text-accent-foreground"
        )}
      >
        <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
          <Check
            className={cn(
              "h-4 w-4 transition-opacity",
              selected ? "opacity-100 text-primary" : "opacity-0"
            )}
            aria-hidden
          />
        </span>
        <div className="flex min-w-0 flex-1 items-start gap-2.5">
          {tool.logoUrl ? (
            <div className="mt-0.5 shrink-0">
              <img
                src={tool.logoUrl}
                alt={tool.title}
                width={18}
                height={18}
                className="object-contain"
              />
            </div>
          ) : null}
          <div className="min-w-0 flex-1 space-y-0.5">
            <div className="flex min-w-0 items-center gap-1.5">
              <span className="truncate font-medium leading-tight">
                {tool.title}
              </span>
              {tool.kind === "integration" &&
                "alias" in tool &&
                tool.alias ? (
                  <IntegrationMetaBadge
                    variant="alias"
                    title={tool.alias}
                    className="max-w-[7rem] shrink-0 px-1.5 py-0 text-[10px] leading-4"
                  >
                    {tool.alias}
                  </IntegrationMetaBadge>
                ) : null}
            </div>
            {tool.subtitle ? (
              <p className="truncate text-xs text-muted-foreground leading-tight">
                {tool.subtitle}
              </p>
            ) : null}
          </div>
        </div>
        {isExpired ? (
          <Badge
            variant="outline"
            className="mt-0.5 shrink-0 rounded-full border-amber-200 bg-amber-50 px-1.5 py-0 text-[10px] font-medium uppercase text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
          >
            Expired
          </Badge>
        ) : isActive ? (
          <Badge
            variant="outline"
            className="mt-0.5 shrink-0 rounded-full border-green-200 bg-green-50 px-1.5 py-0 text-[10px] font-medium uppercase text-green-800 dark:border-green-800 dark:bg-green-950/40 dark:text-green-200"
          >
            Active
          </Badge>
        ) : null}
        {isAttached && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              handleSelect(tool.id);
            }}
            aria-label={`Remove ${tool.title}`}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    );
  };

  const triggerLabel = React.useMemo(() => {
    if (triggerPlaceholder) return triggerPlaceholder;
    if (attachedGroupTools.length === 0) {
      return variant === "integrations"
        ? "Select apps to connect"
        : variant === "custom_apps"
          ? "Select custom apps to connect"
          : "Select apps to connect";
    }
    if (attachedGroupTools.length === 1) {
      const tool = attachedGroupTools[0];
      if (tool.kind === "integration" && "alias" in tool && tool.alias) {
        return formatIntegrationDisplayName(tool.title, tool.alias);
      }
      return tool.title;
    }
    return `${attachedGroupTools.length} apps selected`;
  }, [attachedGroupTools, triggerPlaceholder, variant]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between h-10", className)}
          disabled={isLoading && attachedGroupTools.length === 0}
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-2"
        align="start"
        sideOffset={4}
        onWheel={(e) => e.stopPropagation()}
      >
        <Input
          placeholder={
            variant === "integrations"
              ? "Search integrations..."
              : variant === "custom_apps"
                ? "Search custom apps..."
                : "Search apps..."
          }
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-8 mb-2 focus-visible:ring-0"
          startIcon={
            <Search className="h-4 w-4 absolute text-muted-foreground left top-1/2 transform -translate-y-1/2" />
          }
        />
        <div
          className="max-h-72 overflow-y-auto overscroll-contain scroll-smooth"
          onWheel={(e) => e.stopPropagation()}
        >
          {isLoading &&
          attachedGroupTools.length === 0 &&
          !isFetchingNextPage &&
          !isFetchingNextCustomAppsPage ? (
            <div className="p-4 text-center">
              <Loader2 className="h-6 w-6 animate-spin mx-auto" />
              <p className="text-sm text-muted-foreground mt-2">
                {variant === "integrations"
                  ? "Loading integrations..."
                  : variant === "custom_apps"
                    ? "Loading custom apps..."
                    : "Loading apps..."}
              </p>
            </div>
          ) : !hasFilteredTools ? (
            <div className="flex flex-col items-center py-4">
              <InboxIcon className="h-8 w-8 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">
                {variant === "integrations"
                  ? "No integrations found"
                  : variant === "custom_apps"
                    ? "No custom apps found"
                    : "No apps found"}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredAttachedTools.length > 0 && (
                <div>
                  <div className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Attached
                  </div>
                  <div className="space-y-1">
                    {filteredAttachedTools.map((tool) =>
                      renderToolRow(tool, "attached")
                    )}
                  </div>
                </div>
              )}
              {filteredAvailableTools.length > 0 && (
                <div>
                  <div className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Available
                  </div>
                  <div className="space-y-1">
                    {filteredAvailableTools.map((tool) =>
                      renderToolRow(tool, "available")
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          <div
            ref={ref}
            className="w-full p-2 flex items-center justify-center min-h-[2rem]"
          >
            {(isFetchingNextPage || isFetchingNextCustomAppsPage) && (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
